import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumAlcoholType } from "./enums/enumAlcoholType.generated";
import { EnumStandardOrder } from "./enums/enumStandardOrder.generated";
import { EnumStockAdjustDutyImplication } from "./enums/enumStockAdjustDutyImplication.generated";

// Model Objects

export class AlcoholDutyFieldRequirement extends ModelObject<AlcoholDutyFieldRequirement> {

    public show: boolean = false;
    public required: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.show = jsonSerializedObject.show;
          this.required = jsonSerializedObject.required;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.show = this.show;
        toRet.required = this.required;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class BooleanFieldRequirement extends ModelObject<BooleanFieldRequirement> {

    public show: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.show = jsonSerializedObject.show;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.show = this.show;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class AlcoholDutyHeaderRequirements extends ModelObject<AlcoholDutyHeaderRequirements> {

    public standardOrderType: AlcoholDutyFieldRequirement = new AlcoholDutyFieldRequirement();
    public germanBeerDutyMovementType: AlcoholDutyFieldRequirement = new AlcoholDutyFieldRequirement();
    public allowedOrderTypes: (EnumStandardOrder)[] = [];
    public liquorLicenceNumber: AlcoholDutyFieldRequirement = new AlcoholDutyFieldRequirement();
    public eireDutyMovementType: AlcoholDutyFieldRequirement = new AlcoholDutyFieldRequirement();
    public allowDraughtRelief: BooleanFieldRequirement = new BooleanFieldRequirement();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.standardOrderType = new AlcoholDutyFieldRequirement(jsonSerializedObject.standardOrderType);
          this.germanBeerDutyMovementType = new AlcoholDutyFieldRequirement(jsonSerializedObject.germanBeerDutyMovementType);
          this.allowedOrderTypes = jsonSerializedObject.allowedOrderTypes.map((v: any) => EnumStandardOrder.getByValue(v));
          this.liquorLicenceNumber = new AlcoholDutyFieldRequirement(jsonSerializedObject.liquorLicenceNumber);
          this.eireDutyMovementType = new AlcoholDutyFieldRequirement(jsonSerializedObject.eireDutyMovementType);
          this.allowDraughtRelief = new BooleanFieldRequirement(jsonSerializedObject.allowDraughtRelief);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.standardOrderType = this.standardOrderType.toJsonSerializedObject();
        toRet.germanBeerDutyMovementType = this.germanBeerDutyMovementType.toJsonSerializedObject();
        toRet.allowedOrderTypes = this.allowedOrderTypes.map(v => v.value);
        toRet.liquorLicenceNumber = this.liquorLicenceNumber.toJsonSerializedObject();
        toRet.eireDutyMovementType = this.eireDutyMovementType.toJsonSerializedObject();
        toRet.allowDraughtRelief = this.allowDraughtRelief.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "standardOrderType", rule: (v: any) => !!v || "standardOrderType is required"},
            {field: "standardOrderType", rule: (v: any) => v.validate()},
            {field: "germanBeerDutyMovementType", rule: (v: any) => !!v || "germanBeerDutyMovementType is required"},
            {field: "germanBeerDutyMovementType", rule: (v: any) => v.validate()},
            {field: "liquorLicenceNumber", rule: (v: any) => !!v || "liquorLicenceNumber is required"},
            {field: "liquorLicenceNumber", rule: (v: any) => v.validate()},
            {field: "eireDutyMovementType", rule: (v: any) => !!v || "eireDutyMovementType is required"},
            {field: "eireDutyMovementType", rule: (v: any) => v.validate()},
            {field: "allowDraughtRelief", rule: (v: any) => !!v || "allowDraughtRelief is required"},
            {field: "allowDraughtRelief", rule: (v: any) => v.validate()},
    ];
}


export class AlcoholDutyLineRequirements extends ModelObject<AlcoholDutyLineRequirements> {

    public abv: AlcoholDutyFieldRequirement = new AlcoholDutyFieldRequirement();
    public manufacturer: AlcoholDutyFieldRequirement = new AlcoholDutyFieldRequirement();
    public unitDutiableLitres: AlcoholDutyFieldRequirement = new AlcoholDutyFieldRequirement();
    public plato: AlcoholDutyFieldRequirement = new AlcoholDutyFieldRequirement();
    public germanBeerDutyProductType: AlcoholDutyFieldRequirement = new AlcoholDutyFieldRequirement();
    public alcoholType: EnumAlcoholType = EnumAlcoholType.none;
    public isBrewedForPersonalUse: BooleanFieldRequirement = new BooleanFieldRequirement();
    public isDesignedForPump: BooleanFieldRequirement = new BooleanFieldRequirement();
    public finalUnitLitres: AlcoholDutyFieldRequirement = new AlcoholDutyFieldRequirement();
    public spiritDutyExemptionReason: AlcoholDutyFieldRequirement = new AlcoholDutyFieldRequirement();
    public packageType: AlcoholDutyFieldRequirement = new AlcoholDutyFieldRequirement();
    public dateOfProductionDutyRate: AlcoholDutyFieldRequirement = new AlcoholDutyFieldRequirement();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.abv = new AlcoholDutyFieldRequirement(jsonSerializedObject.abv);
          this.manufacturer = new AlcoholDutyFieldRequirement(jsonSerializedObject.manufacturer);
          this.unitDutiableLitres = new AlcoholDutyFieldRequirement(jsonSerializedObject.unitDutiableLitres);
          this.plato = new AlcoholDutyFieldRequirement(jsonSerializedObject.plato);
          this.germanBeerDutyProductType = new AlcoholDutyFieldRequirement(jsonSerializedObject.germanBeerDutyProductType);
          this.alcoholType = EnumAlcoholType.getByValue(jsonSerializedObject.alcoholType, this.alcoholType);
          this.isBrewedForPersonalUse = new BooleanFieldRequirement(jsonSerializedObject.isBrewedForPersonalUse);
          this.isDesignedForPump = new BooleanFieldRequirement(jsonSerializedObject.isDesignedForPump);
          this.finalUnitLitres = new AlcoholDutyFieldRequirement(jsonSerializedObject.finalUnitLitres);
          this.spiritDutyExemptionReason = new AlcoholDutyFieldRequirement(jsonSerializedObject.spiritDutyExemptionReason);
          this.packageType = new AlcoholDutyFieldRequirement(jsonSerializedObject.packageType);
          this.dateOfProductionDutyRate = new AlcoholDutyFieldRequirement(jsonSerializedObject.dateOfProductionDutyRate);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.abv = this.abv.toJsonSerializedObject();
        toRet.manufacturer = this.manufacturer.toJsonSerializedObject();
        toRet.unitDutiableLitres = this.unitDutiableLitres.toJsonSerializedObject();
        toRet.plato = this.plato.toJsonSerializedObject();
        toRet.germanBeerDutyProductType = this.germanBeerDutyProductType.toJsonSerializedObject();
        toRet.alcoholType = this.alcoholType.value;
        toRet.isBrewedForPersonalUse = this.isBrewedForPersonalUse.toJsonSerializedObject();
        toRet.isDesignedForPump = this.isDesignedForPump.toJsonSerializedObject();
        toRet.finalUnitLitres = this.finalUnitLitres.toJsonSerializedObject();
        toRet.spiritDutyExemptionReason = this.spiritDutyExemptionReason.toJsonSerializedObject();
        toRet.packageType = this.packageType.toJsonSerializedObject();
        toRet.dateOfProductionDutyRate = this.dateOfProductionDutyRate.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "abv", rule: (v: any) => !!v || "abv is required"},
            {field: "abv", rule: (v: any) => v.validate()},
            {field: "manufacturer", rule: (v: any) => !!v || "manufacturer is required"},
            {field: "manufacturer", rule: (v: any) => v.validate()},
            {field: "unitDutiableLitres", rule: (v: any) => !!v || "unitDutiableLitres is required"},
            {field: "unitDutiableLitres", rule: (v: any) => v.validate()},
            {field: "plato", rule: (v: any) => !!v || "plato is required"},
            {field: "plato", rule: (v: any) => v.validate()},
            {field: "germanBeerDutyProductType", rule: (v: any) => !!v || "germanBeerDutyProductType is required"},
            {field: "germanBeerDutyProductType", rule: (v: any) => v.validate()},
            {field: "isBrewedForPersonalUse", rule: (v: any) => !!v || "isBrewedForPersonalUse is required"},
            {field: "isBrewedForPersonalUse", rule: (v: any) => v.validate()},
            {field: "isDesignedForPump", rule: (v: any) => !!v || "isDesignedForPump is required"},
            {field: "isDesignedForPump", rule: (v: any) => v.validate()},
            {field: "finalUnitLitres", rule: (v: any) => !!v || "finalUnitLitres is required"},
            {field: "finalUnitLitres", rule: (v: any) => v.validate()},
            {field: "spiritDutyExemptionReason", rule: (v: any) => !!v || "spiritDutyExemptionReason is required"},
            {field: "spiritDutyExemptionReason", rule: (v: any) => v.validate()},
            {field: "packageType", rule: (v: any) => !!v || "packageType is required"},
            {field: "packageType", rule: (v: any) => v.validate()},
            {field: "dateOfProductionDutyRate", rule: (v: any) => !!v || "dateOfProductionDutyRate is required"},
            {field: "dateOfProductionDutyRate", rule: (v: any) => v.validate()},
    ];
}


export class StockLocationDutyRequirements extends ModelObject<StockLocationDutyRequirements> {

    public warehouseTaxCode: AlcoholDutyFieldRequirement = new AlcoholDutyFieldRequirement();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.warehouseTaxCode = new AlcoholDutyFieldRequirement(jsonSerializedObject.warehouseTaxCode);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.warehouseTaxCode = this.warehouseTaxCode.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "warehouseTaxCode", rule: (v: any) => !!v || "warehouseTaxCode is required"},
            {field: "warehouseTaxCode", rule: (v: any) => v.validate()},
    ];
}


export class StockLocationsDutyRequirements extends ModelObject<StockLocationsDutyRequirements> {

    public bondedRequirements: StockLocationDutyRequirements = new StockLocationDutyRequirements();
    public nonBondedRequirements: StockLocationDutyRequirements = new StockLocationDutyRequirements();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.bondedRequirements = new StockLocationDutyRequirements(jsonSerializedObject.bondedRequirements);
          this.nonBondedRequirements = new StockLocationDutyRequirements(jsonSerializedObject.nonBondedRequirements);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.bondedRequirements = this.bondedRequirements.toJsonSerializedObject();
        toRet.nonBondedRequirements = this.nonBondedRequirements.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "bondedRequirements", rule: (v: any) => !!v || "bondedRequirements is required"},
            {field: "bondedRequirements", rule: (v: any) => v.validate()},
            {field: "nonBondedRequirements", rule: (v: any) => !!v || "nonBondedRequirements is required"},
            {field: "nonBondedRequirements", rule: (v: any) => v.validate()},
    ];
}


export class StockAdjustmentDutyRequirements extends ModelObject<StockAdjustmentDutyRequirements> {

    public allowedDutyImplications: (EnumStockAdjustDutyImplication)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.allowedDutyImplications = jsonSerializedObject.allowedDutyImplications.map((v: any) => EnumStockAdjustDutyImplication.getByValue(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.allowedDutyImplications = this.allowedDutyImplications.map(v => v.value);
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class PackageTypeDutyRequirements extends ModelObject<PackageTypeDutyRequirements> {

    public categoryType: AlcoholDutyFieldRequirement = new AlcoholDutyFieldRequirement();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.categoryType = new AlcoholDutyFieldRequirement(jsonSerializedObject.categoryType);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.categoryType = this.categoryType.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "categoryType", rule: (v: any) => !!v || "categoryType is required"},
            {field: "categoryType", rule: (v: any) => v.validate()},
    ];
}


export class OutletDutyRequirements extends ModelObject<OutletDutyRequirements> {

    public isWholesaler: BooleanFieldRequirement = new BooleanFieldRequirement();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.isWholesaler = new BooleanFieldRequirement(jsonSerializedObject.isWholesaler);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.isWholesaler = this.isWholesaler.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "isWholesaler", rule: (v: any) => !!v || "isWholesaler is required"},
            {field: "isWholesaler", rule: (v: any) => v.validate()},
    ];
}


export class DutyRulesService extends ServiceBase {

  public async getAllowedAlcoholTypes(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(EnumAlcoholType)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.allowedAlcoholTypes.map((v: any) => EnumAlcoholType.getByValue(v));;
    return this.makeJsonRequest("dutyRules/v1/getAllowedAlcoholTypes", req, false, extractor, opts);
  };

  public async getAlcoholDutyHeaderRequirements(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<AlcoholDutyHeaderRequirements> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => new AlcoholDutyHeaderRequirements(response.requirements);;
    return this.makeJsonRequest("dutyRules/v1/getAlcoholDutyHeaderRequirements", req, false, extractor, opts);
  };

  public async getAlcoholDutyLineRequirements(alcoholType: EnumAlcoholType, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<AlcoholDutyLineRequirements> {
    const req = {
    tenantId:api.tenantId.toString(),
    alcoholType:alcoholType.value,
    };
    const extractor = (response:any) => new AlcoholDutyLineRequirements(response.requirements);;
    return this.makeJsonRequest("dutyRules/v1/getAlcoholDutyLineRequirements", req, false, extractor, opts);
  };

  public async getStockLocationDutyRequirements(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<StockLocationsDutyRequirements> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => new StockLocationsDutyRequirements(response.requirements);;
    return this.makeJsonRequest("dutyRules/v1/getStockLocationDutyRequirements", req, false, extractor, opts);
  };

  public async getAlcoholDutyLineRequirementsForTypes(alcoholTypes: readonly EnumAlcoholType[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(AlcoholDutyLineRequirements)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    alcoholTypes:alcoholTypes.map(v => v.value),
    };
    const extractor = (response:any) => response.requirements.map((v: any) => new AlcoholDutyLineRequirements(v));;
    return this.makeJsonRequest("dutyRules/v1/getAlcoholDutyLineRequirementsForTypes", req, false, extractor, opts);
  };

  public async isTransferOfAlcoholFromNonBondedToBondedAllowed(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<boolean> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.isAllowed;;
    return this.makeJsonRequest("dutyRules/v1/isTransferOfAlcoholFromNonBondedToBondedAllowed", req, false, extractor, opts);
  };

  public async arePurchaseOrdersReportedForDuty(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<boolean> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.purchaseOrdersAreReportedForDuty;;
    return this.makeJsonRequest("dutyRules/v1/arePurchaseOrdersReportedForDuty", req, false, extractor, opts);
  };

  public async getStockAdjustmentDutyRequirements(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<StockAdjustmentDutyRequirements> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => new StockAdjustmentDutyRequirements(response.requirements);;
    return this.makeJsonRequest("dutyRules/v1/getStockAdjustmentDutyRequirements", req, false, extractor, opts);
  };

  public async getPackageTypeDutyRequirements(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<PackageTypeDutyRequirements> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => new PackageTypeDutyRequirements(response.requirements);;
    return this.makeJsonRequest("dutyRules/v1/getPackageTypeDutyRequirements", req, false, extractor, opts);
  };

  public async getOutletDutyRequirements(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<OutletDutyRequirements> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => new OutletDutyRequirements(response.requirements);;
    return this.makeJsonRequest("dutyRules/v1/getOutletDutyRequirements", req, false, extractor, opts);
  };
}

export const dutyRulesService = new DutyRulesService();